import React, { useMemo, useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  DialogActions,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronDown, FileText } from 'lucide-react';
import { AlineButton, AlineDialog } from 'src/components';
import { makeStyles } from 'tss-react/mui';

interface BulkExportSkippedView {
  documentId: string;
  entryName: string;
  reason: string;
}

interface BulkExportSkippedDialogProps {
  onClose: () => void;
  open: boolean;
  /**
   * True total of skipped docs from the activity. May exceed
   * `skippedDocuments.length` when the DB truncates the sample.
   */
  skippedCount: number;
  /**
   * Capped sample of skipped docs (up to `MAX_SKIPPED_DOCUMENTS_PERSISTED`,
   * currently 1000). Used to populate the per-category lists. The headline
   * uses `skippedCount` instead so a 25K-doc all-failed export still shows
   * the real total, not the truncated `skippedDocuments.length`.
   */
  skippedDocuments: BulkExportSkippedView[];
  /**
   * Called when the user clicks Retry. Server-side retry isn't wired up yet
   * (Phase 3) — pass undefined to render Retry as disabled.
   */
  onRetry?: () => void;
}

type CategoryKey = 'missingPermissions' | 'unsupportedFileType' | 'missingLabels' | 'other';

interface CategoryDef {
  description: string;
  /** Heuristic match against the activity's free-form `reason` string. */
  matchers: RegExp[];
  title: string;
}

// Map free-form skip reasons emitted by the assemble activity into the three
// product-facing categories from the design. Anything that doesn't match
// falls into "Other" so we never lose a doc — once the backend grows a
// typed skip-reason enum we can drop the regex matching.
const CATEGORIES: Record<CategoryKey, CategoryDef> = {
  missingLabels: {
    description: 'These documents require labels before export.',
    matchers: [/missing label/i, /labels required/i],
    title: 'Missing labels',
  },
  missingPermissions: {
    description: 'These documents are restricted and cannot be exported.',
    matchers: [
      /active workflow/i,
      /not found at export time/i,
      /not authorized/i,
      /restricted/i,
      /permission/i,
    ],
    title: 'Missing permissions',
  },
  other: {
    description: 'These documents could not be exported.',
    matchers: [],
    title: 'Other',
  },
  unsupportedFileType: {
    description: 'Some file formats are not currently supported.',
    matchers: [
      /no file version/i,
      /missing s3Location/i,
      /cannot convert/i,
      /unsupported/i,
      /static file/i,
      /unknown mimetype/i,
    ],
    title: 'Unsupported file type',
  },
};

// Display order matches the design (Missing labels → Unsupported → Permissions
// → Other). Categories with zero docs are hidden.
const CATEGORY_ORDER: CategoryKey[] = [
  'missingLabels',
  'unsupportedFileType',
  'missingPermissions',
  'other',
];

function categorize(reason: string): CategoryKey {
  for (const key of CATEGORY_ORDER) {
    if (CATEGORIES[key].matchers.some(m => m.test(reason))) return key;
  }
  return 'other';
}

const useStyles = makeStyles()(theme => ({
  accordion: {
    // Avoid the expanded-state margin shift that fights `disableGutters`.
    '&.Mui-expanded': {
      '&:not(:last-child)': { marginBottom: theme.spacing(1) },
      margin: 0,
    },

    // Override MUI's default top-border pseudo-element + drop the divider line.
    '&:before': { display: 'none' },

    '&:not(:last-child)': { marginBottom: theme.spacing(1) },
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    boxShadow: 'none',
  },
  accordionContainer: {
    // Pull the accordions out of AlineDialog's flex `space-between` layout
    // so expanding one doesn't stretch / squash the others.
    display: 'block',
    marginBottom: theme.spacing(2),
  },
  accordionDetails: {
    paddingBottom: theme.spacing(2),
    paddingTop: 0,
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      '&.Mui-expanded': { margin: `${theme.spacing(1.5)} 0` },
      margin: `${theme.spacing(1.5)} 0`,
    },
    '&.Mui-expanded': { minHeight: 64 },
    minHeight: 64,
  },
  description: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  docIcon: {
    color: theme.palette.primary.main,
    flexShrink: 0,
    marginTop: 2,
  },
  docList: {
    // Cap the rendered list inside an expanded accordion so 800 rows can't
    // shove the dialog past the viewport. The "Show all" link inside the
    // capped list lets the user opt in if they actually want the whole thing.
    maxHeight: 320,
    overflowY: 'auto',
  },
  docName: {
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  docReason: {
    color: theme.palette.text.secondary,
    display: 'block',
    fontSize: 12,
    lineHeight: 1.35,
    wordBreak: 'break-word',
  },
  paper: {
    width: 680,
  },
  primaryButton: {
    marginLeft: theme.spacing(1),
  },
  showAllLink: {
    '&:hover': { textDecoration: 'underline' },
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: 500,
    paddingTop: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2.5),
  },
  summaryHeader: {
    fontWeight: 600,
  },
}));

export function BulkExportSkippedDialog({
  onClose,
  onRetry,
  open,
  skippedCount,
  skippedDocuments,
}: BulkExportSkippedDialogProps): JSX.Element {
  const { classes } = useStyles();
  const [expanded, setExpanded] = useState<CategoryKey | null>(null);

  const groups = useMemo(() => {
    const byCategory = new Map<CategoryKey, BulkExportSkippedView[]>();
    for (const entry of skippedDocuments) {
      const key = categorize(entry.reason);
      const list = byCategory.get(key) ?? [];
      list.push(entry);
      byCategory.set(key, list);
    }
    return CATEGORY_ORDER.map(key => ({
      def: CATEGORIES[key],
      docs: byCategory.get(key) ?? [],
      key,
    })).filter(g => g.docs.length > 0);
  }, [skippedDocuments]);

  // Use the activity-side count (may exceed skippedDocuments.length when the
  // sample is truncated to MAX_SKIPPED_DOCUMENTS_PERSISTED).
  const total = skippedCount;
  const sampleSize = skippedDocuments.length;
  const isTruncated = sampleSize < total;

  return (
    <AlineDialog
      classes={{ paper: classes.paper }}
      open={open}
      title="Skipped documents"
      onClose={onClose}
    >
      <Typography className={classes.subtitle} variant="body2">
        {total} document{total === 1 ? '' : 's'} could not be exported.
        {isTruncated &&
          ` Showing details for the first ${sampleSize}; see the manifest in the ZIP for the full list.`}
      </Typography>

      <Box className={classes.accordionContainer}>
        {groups.map(({ def, docs, key }) => (
          /**
           * `disableGutters` keeps the summary height stable across
           * collapse/expand. `slotProps.transition` uses a fast 150ms
           * transition and keeps content mounted on exit — rendering 800+
           * Lucide icons inside a MUI Collapse animation was forcing
           * layout/paint for every row before the animation could start (the
           * previous "very slow" complaint); keeping content mounted also
           * makes the second expand instant.
           */
          <Accordion
            key={key}
            disableGutters
            className={classes.accordion}
            expanded={expanded === key}
            slotProps={{ transition: { timeout: 150, unmountOnExit: false } }}
            onChange={(_, isExpanded) => setExpanded(isExpanded ? key : null)}
          >
            <AccordionSummary
              className={classes.accordionSummary}
              expandIcon={<ChevronDown size={20} />}
            >
              <Box>
                <Typography className={classes.summaryHeader} variant="body1">
                  {def.title} ({docs.length})
                </Typography>
                <Typography className={classes.description} variant="body2">
                  {def.description}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <SkippedDocList classes={classes} docs={docs} />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <DialogActions>
        <AlineButton color="primary" variant="outlined" onClick={onClose}>
          Cancel
        </AlineButton>
        <AlineButton
          className={classes.primaryButton}
          color="primary"
          disabled={!onRetry}
          variant="contained"
          onClick={() => onRetry?.()}
        >
          Retry
        </AlineButton>
      </DialogActions>
    </AlineDialog>
  );
}

type StyleClasses = ReturnType<typeof useStyles>['classes'];

// Cap how many rows we render per accordion before requiring the user to
// click "Show all". Each row is a Stack + Lucide icon + Typography; rendering
// 800+ at once forces the Collapse animation to lay out every row before it
// can start, which was the source of the slow expand/collapse. 50 covers the
// useful debugging case (group by reason, scan the first few) and a single
// click reveals the rest.
const INITIAL_VISIBLE = 50;

function SkippedDocList(props: {
  classes: StyleClasses;
  docs: BulkExportSkippedView[];
}): JSX.Element {
  const { classes, docs } = props;
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? docs : docs.slice(0, INITIAL_VISIBLE);
  const hasMore = docs.length > INITIAL_VISIBLE;

  return (
    <Stack className={classes.docList} spacing={1.25}>
      {visible.map(doc => (
        <Stack key={doc.documentId} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <FileText className={classes.docIcon} size={16} />
          <Box>
            <Typography className={classes.docName} variant="body2">
              {doc.entryName || 'Untitled document'}
            </Typography>
            {doc.reason && (
              <Typography className={classes.docReason} variant="caption">
                {doc.reason}
              </Typography>
            )}
          </Box>
        </Stack>
      ))}
      {hasMore && !showAll && (
        <Typography
          className={classes.showAllLink}
          variant="body2"
          onClick={() => setShowAll(true)}
        >
          Show all {docs.length}
        </Typography>
      )}
    </Stack>
  );
}
